import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthState } from '../store/models/auth-state.interface';
import { ActionsSubject, Store } from '@ngrx/store';
import { login, loginFailure } from '../store/actions/auth.actions';
import * as AuthSelectors from '../store/selectors/auth.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';
import { AppRoutesEnum } from '../../enums/app-routes.enum';
import { ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
    selector: 'aft-login',
    templateUrl: './login.component.html',
    standalone: false,
})
export class LoginComponent implements OnInit {
  public showPassword = false;
  public loginForm: FormGroup = this._fb.group({
    login: ['', Validators.required],
    password: ['', Validators.required],
    code: '',
  });

  public loginDisabled$ = this._store.select(AuthSelectors.disableLoginForm);
  public has2FAInit$ = this._store.select(AuthSelectors.has2FAInit);
  public has2FAVerify$ = this._store.select(AuthSelectors.has2FAVerify);
  public error$ = this._store.select(AuthSelectors.errorSelector);
  public isVerifyRequired = false;

  protected errorMsg: string;

  constructor(
    private readonly _fb: FormBuilder,
    private _router: Router,
    private _store: Store<AuthState>,
    private _actionsStream: ActionsSubject,
    private _translate: TranslateService,
  ) {
    this.loginDisabled$
      .pipe(untilDestroyed(this))
      .subscribe((loginDisabled) => {
        if (loginDisabled) {
          this.loginForm.get('login').disable();
          this.loginForm.get('password').disable();
        } else {
          this.loginForm.get('login').enable();
          this.loginForm.get('password').enable();
        }
      });

    this._actionsStream
      .pipe(
        untilDestroyed(this),
        ofType(loginFailure),
      )
      .subscribe(() => {
        const { code } = this.loginForm.getRawValue();

        if (code) {
          this.errorMsg = this._translate.instant('loginOtpError');
        } else {
          this.errorMsg = this._translate.instant('loginError');
        }
      });

    this.loginForm.valueChanges
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.errorMsg = '';
      });
  }

  public ngOnInit(): void {
    this.has2FAInit$.pipe(untilDestroyed(this)).subscribe(otpInitData => {
      if ( otpInitData ) {
        const { password, login } = this.loginForm.getRawValue();
        this._router.navigate([AppRoutesEnum.Otp], { state: { otpInitData, password, login } });
      }
    });

    this.has2FAVerify$.pipe(untilDestroyed(this)).subscribe( isVerifyRequired => {
      this.isVerifyRequired = isVerifyRequired;
      if ( this.isVerifyRequired ) this.loginForm.get('code').addValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]);
    });
  }

  public login(): void {
    if ( this.loginForm.valid ) {
      const loginData = this.loginForm.getRawValue();

      if ( this.isVerifyRequired ) {
        this._store.dispatch(login({
          username: loginData.login,
          password: loginData.password,
          otp_code: loginData.code,
        }));
      } else {
        this.errorMsg = '';
        this._store.dispatch(login({ username: loginData.login, password: loginData.password }));
      }
    }
  }
}
